// src/modules/app-views/fiscal-config/pages/Document/tabs/CertificateTab/FiscalIdCard.jsx
import { Card, Row, Col, Form, Divider } from 'antd';
import translateX from 'utils/translateX';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import SimpleSelect from 'components/SimpleComponents/SimpleSelect';
import SimpleNumber from 'components/SimpleComponents/SimpleNumber';
import useZustand from 'hooks/useZustand';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import {
  TAX_REGIME_OPTIONS,
  ICMS_CST_OPTIONS,
  ICMS_CSOSN_OPTIONS,
  PIS_CST_OPTIONS,
  COFINS_CST_OPTIONS,
  IPI_CST_OPTIONS,
  ALLOWED_DEFAULT_ICMS_CST,
  ALLOWED_DEFAULT_ICMS_CSOSN,
} from './constants';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const RESOURCE_NFE = 'moduleresources_nfe';
const RESOURCE_NFCE = 'moduleresources_nfce';
const RESOURCE_NFSE = 'moduleresources_nfse';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const FiscalIdCard = ({ displayCertificate }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedSubscription = useZustand('auth', 'subscription');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS
  // ─────────────────────────────────────────────────────────────────────────────
  // Watch taxRegime to conditionally show CSOSN vs CST options for ICMS
  const taxRegime = Form.useWatch(['fiscalData', 'taxRegime']);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  // Check subscription resources to determine required fields
  const subscriptionResources = storedSubscription?.resources || [];
  const hasNfeResource = subscriptionResources.includes(RESOURCE_NFE);
  const hasNfceResource = subscriptionResources.includes(RESOURCE_NFCE);
  const hasNfseResource = subscriptionResources.includes(RESOURCE_NFSE);

  // Determine which fields are required based on resources
  // IE and cityCode required for NFe or NFCe
  const requireStateRegistration = hasNfeResource || hasNfceResource;
  // Municipal registration and CNAE required for NFSe
  const requireMunicipalData = hasNfseResource;

  // Simples Nacional (CRT 1/2) uses CSOSN, Regime Normal (CRT 3) uses CST
  const isSimples = taxRegime === 'simples_nacional' || taxRegime === 'simples_nacional_excesso';

  // Filter ICMS options: company defaults cannot include ST-specific codes
  // ST codes depend on product, not company
  const icmsOptions = isSimples
    ? ICMS_CSOSN_OPTIONS.filter(opt => ALLOWED_DEFAULT_ICMS_CSOSN.includes(opt.value))
    : ICMS_CST_OPTIONS.filter(opt => ALLOWED_DEFAULT_ICMS_CST.includes(opt.value));
  const icmsLabel = isSimples ? 'icms_csosn' : 'icms_cst';
  const icmsFieldName = isSimples ? 'fiscalData.defaultIcmsCsosn' : 'fiscalData.defaultIcmsCst';

  // ─────────────────────────────────────────────────────────────────────────────
  // UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Card title={<div className="text-muted">{translateX('fiscal_identification')}</div>}>
      {/* Row 1: Identification */}
      <Row gutter={ROW_GUTTER}>
        {/* Tax Regime */}
        <Col xs={24} sm={8}>
          <SimpleSelect
            name="fiscalData.taxRegime"
            label="tax_regime"
            options={TAX_REGIME_OPTIONS}
            required={!!displayCertificate}
          />
        </Col>

        {/* State Registration (IE) - Required for NFe/NFCe */}
        <Col xs={24} sm={8}>
          <SimpleInput
            name="fiscalData.stateRegistration"
            label="state_registration"
            placeholder="000.000.000.000"
            required={requireStateRegistration}
          />
        </Col>

        {/* Municipality IBGE Code (cMun) - Required for NFe/NFCe */}
        <Col xs={24} sm={8}>
          <SimpleInput
            name="address.cityCode"
            label="municipality_ibge_code"
            placeholder="0000000"
            maxLength={7}
            required={requireStateRegistration}
            tooltip="ibge_code_tooltip"
          />
        </Col>
      </Row>

      {/* Row 2: Codes */}
      <Row gutter={ROW_GUTTER} style={{ marginTop: 16 }}>
        {/* SUFRAMA */}
        <Col xs={24} sm={8}>
          <SimpleInput name="fiscalData.suframa" label="suframa" placeholder="000000000" />
        </Col>

        {/* Municipal Registration (IM) - Required for NFSe */}
        <Col xs={24} sm={8}>
          <SimpleInput
            name="fiscalData.municipalRegistration"
            label="municipal_registration"
            placeholder="00000000"
            required={requireMunicipalData}
          />
        </Col>

        {/* CNAE - Required for NFSe */}
        <Col xs={24} sm={8}>
          <SimpleInput
            name="fiscalData.mainCnae"
            label="cnae"
            placeholder="0000000"
            maxLength={7}
            required={requireMunicipalData}
            tooltip="cnae_tooltip"
          />
        </Col>

        {/* NOTE: CFOP removed - CFOP depends on operation type, not company defaults */}
        {/* NOTE: ICMS Origin removed - Origin depends on product, not company */}
      </Row>

      <Divider style={{ margin: '16px 0' }} />

      {/* Row 3: Default CST Codes - Visibility based on Tax Regime */}
      <Row gutter={ROW_GUTTER}>
        {/* ICMS CST/CSOSN - Always visible, switches between CST (CRT 3) and CSOSN (CRT 1/2) */}
        <Col xs={24} sm={12} lg={6}>
          <SimpleSelect
            name={icmsFieldName}
            label={icmsLabel}
            options={icmsOptions}
            tooltip="default_tax_for_items"
            required={true}
            allowClear
          />
        </Col>

        {/* PIS CST - Only for Regime Normal (CRT 3) */}
        {!isSimples && (
          <Col xs={24} sm={12} lg={6}>
            <SimpleSelect
              name="fiscalData.defaultPisCst"
              label="pis_cst"
              required={true}
              options={PIS_CST_OPTIONS}
              tooltip="default_tax_for_items"
              allowClear
            />
          </Col>
        )}

        {/* COFINS CST - Only for Regime Normal (CRT 3) */}
        {!isSimples && (
          <Col xs={24} sm={12} lg={6}>
            <SimpleSelect
              name="fiscalData.defaultCofinsCst"
              label="cofins_cst"
              required={true}
              options={COFINS_CST_OPTIONS}
              tooltip="default_tax_for_items"
              allowClear
            />
          </Col>
        )}

        {/* IPI CST - Only when NFe resource is available (industrial products) */}
        {hasNfeResource && (
          <Col xs={24} sm={12} lg={6}>
            <SimpleSelect
              name="fiscalData.defaultIpiCst"
              label="ipi_cst"
              options={IPI_CST_OPTIONS}
              tooltip="default_tax_for_items"
              allowClear
            />
          </Col>
        )}
      </Row>

      {/* Row 4: Default Rates - Visibility based on Tax Regime */}
      <Row gutter={ROW_GUTTER} style={{ marginTop: 16 }}>
        {/* Default ICMS Rate - Always visible for CRT 3, conditional for CRT 1/2 (only if NFe) */}
        {(!isSimples || hasNfeResource) && (
          <Col xs={24} sm={12} lg={6}>
            <SimpleNumber
              name="fiscalData.defaultIcmsRate"
              label="icms_rate"
              min={0}
              max={100}
              decimalPlaces={2}
              required={true}
              percentage
              tooltip="default_tax_for_items"
            />
          </Col>
        )}

        {/* Default PIS Rate - Only for Regime Normal (CRT 3) */}
        {!isSimples && (
          <Col xs={24} sm={12} lg={6}>
            <SimpleNumber
              name="fiscalData.defaultPisRate"
              label="pis_rate"
              min={0}
              max={100}
              decimalPlaces={2}
              percentage
              tooltip="default_tax_for_items"
            />
          </Col>
        )}

        {/* Default COFINS Rate - Only for Regime Normal (CRT 3) */}
        {!isSimples && (
          <Col xs={24} sm={12} lg={6}>
            <SimpleNumber
              name="fiscalData.defaultCofinsRate"
              label="cofins_rate"
              min={0}
              max={100}
              decimalPlaces={2}
              percentage
              tooltip="default_tax_for_items"
            />
          </Col>
        )}

        {/* Default IPI Rate - Only when NFe resource is available (industrial products) */}
        {hasNfeResource && (
          <Col xs={24} sm={12} lg={6}>
            <SimpleNumber
              name="fiscalData.defaultIpiRate"
              label="ipi_rate"
              min={0}
              max={100}
              decimalPlaces={2}
              percentage
              tooltip="default_tax_for_items"
            />
          </Col>
        )}
      </Row>
    </Card>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default FiscalIdCard;
